import {
    Box,
    Button,
    Card,
    CardContent,
    Stack,
    Typography,
} from '@mui/material';

/**
 * Encabezado reutilizable que muestra los datos de customFields.
 *
 * Cada campo admite:
 * - label: etiqueta del campo.
 * - value: valor que se mostrará.
 * - secondaryValue: texto complementario opcional.
 * - key: identificador único opcional; por defecto usa label.
 * - md: ancho del campo en escritorio, de 1 a 12 columnas.
 *   Por defecto ocupa 4 columnas, permitiendo 3 campos por fila.
 *   Por ejemplo, md: 8 ocupa dos tercios del ancho disponible.
 *
 * Los campos se distribuyen en orden y pasan a una nueva fila
 * cuando su ancho supera las columnas disponibles.
 * En pantallas menores al breakpoint md, cada campo ocupa todo el ancho.
 */

/**
 * Ejemplo
    const customFields = [
        {
            label: 'NIT',
            value: '1234567-8',
            secondaryValue: '123',
            md: 8
        },
        {
            label: 'Dirección',
            value: 'Zona 1, San Juan Chamelco'
        },
        {
            label: 'Teléfono1',
            value: '5555-1234',
            secondaryValue: '123',
        },
    ]
*/
export const CustomHeader = ({
    backLabel = 'Volver',
    onBack,
    customFields = [],
}) => {
    const renderCustomFields = () => {
        let occupiedColumns = 0;

        return (
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
                    rowGap: 3,
                }}
            >
                {customFields.map((customField) => {
                    const columnSpan = Math.max(
                        1,
                        Math.min(12, Math.trunc(Number(customField.md) || 4))
                    );

                    // Si el campo no cabe, comienza una nueva fila.
                    if (occupiedColumns + columnSpan > 12) {
                        occupiedColumns = 0;
                    }

                    const isFirstInRow = occupiedColumns === 0;

                    occupiedColumns += columnSpan;

                    if (occupiedColumns === 12) {
                        occupiedColumns = 0;
                    }

                    return (
                        <Box
                            key={customField.key ?? customField.label}
                            sx={{
                                gridColumn: {
                                    xs: 'span 12',
                                    md: `span ${columnSpan}`,
                                },
                                minWidth: 0,
                                pl: { xs: 0, md: isFirstInRow ? 0 : 3 },
                                pr: { xs: 0, md: 3 },
                                borderLeft: {
                                    xs: 'none',
                                    md: isFirstInRow ? 'none' : '1px solid #DDE0E4',
                                },
                            }}
                        >
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.75 }}
                        >
                            {customField.label}
                        </Typography>

                        <Typography
                            variant="body1"
                            fontWeight={600}
                            sx={{
                                color: '#20242C',
                                lineHeight: 1.4,
                                overflowWrap: 'anywhere',
                            }}
                        >
                            {customField.value ?? '—'}
                        </Typography>

                        {customField.secondaryValue && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.5, overflowWrap: 'anywhere' }}
                            >
                                {customField.secondaryValue}
                            </Typography>
                        )}
                        </Box>
                    );
                })}
            </Box>
        );
    };

    return (
        <Box sx={{ mb: 3 }}>
            {onBack && (
                <Button
                    variant="text"
                    onClick={onBack}
                    sx={{
                        px: 0,
                        mb: 1,
                        color: '#B18A46',
                        textTransform: 'none',
                        fontWeight: 600,
                    }}
                >
                    ← {backLabel}
                </Button>
            )}

            <Card
                variant="outlined"
                sx={{
                    borderColor: '#E1E4E8',
                    borderTop: '4px solid #B18A46',
                    borderRadius: 2,
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 3px 12px rgba(0, 0, 0, 0.04)',
                }}
            >
                <CardContent
                    sx={{
                        p: { xs: 2, md: 3 },
                        '&:last-child': { pb: { xs: 2, md: 3 } },
                    }}
                >
                    <Stack spacing={2.5}>
                        {customFields.length > 0 && renderCustomFields()}
                    </Stack>
                </CardContent>
            </Card>
        </Box>
    );
};